import React from 'react';

const formatINR = (v: number) => {
    if (!isFinite(v) || isNaN(v)) return '-';
    const nf = new Intl.NumberFormat('en-IN', { maximumFractionDigits: 0 });
    return `₹${nf.format(v)}`;
};

const formatCompact = (v: number) => {
    if (!isFinite(v) || isNaN(v)) return '-';
    if (Math.abs(v) >= 100000) {
        const lakhs = Math.round(v / 100000);
        return `₹${lakhs} L`;
    }
    return formatINR(v);
};

const RealEstateInvestmentCalculator: React.FC = () => {
    const [isRented, setIsRented] = React.useState(true);
    const [purchasePrice, setPurchasePrice] = React.useState<number>(3700000);
    const [currentPrice, setCurrentPrice] = React.useState<number>(4800000);
    const [rentPerMonth, setRentPerMonth] = React.useState<number>(12000);
    const [purchaseDate, setPurchaseDate] = React.useState<string>('2026-04');

    const now = new Date();

    const results = React.useMemo(() => {
        const purchase = Number(purchasePrice) || 0;
        const current = Number(currentPrice) || 0;
        const rent = Number(rentPerMonth) || 0;

        // compute years between purchaseDate (YYYY-MM) and now
        let years = 0;
        try {
            const [y, m] = (purchaseDate || '').split('-').map((s) => Number(s));
            if (y && m) {
                const pd = new Date(y, m - 1, 1);
                const months = (now.getFullYear() - pd.getFullYear()) * 12 + (now.getMonth() - pd.getMonth());
                years = Math.max(0, months / 12);
            }
        } catch (e) {
            years = 0;
        }

        const totalInvestment = current; // current market value as investment
        const totalRentalIncome = isRented ? rent * 12 * Math.floor(years) : 0;
        const totalRentalYears = Math.floor(years);
        const netProfit = current - purchase + totalRentalIncome;
        const roiPerc = purchase > 0 ? (netProfit / purchase) * 100 : 0;

        // annualized (approx) = (current + rentIncome) / purchase)^(1/years) -1
        let annualized = 0;
        if (purchase > 0 && years >= 1) {
            const endValue = current + totalRentalIncome;
            annualized = Math.pow(endValue / purchase, 1 / years) - 1;
        }

        return {
            totalInvestment,
            totalRentalIncome,
            totalRentalYears,
            netProfit,
            roiPerc,
            annualized
        };
    }, [isRented, purchasePrice, currentPrice, rentPerMonth, purchaseDate, now]);

    return (
        <div className="bg-white rounded-2xl border border-slate-200 p-6 md:p-8 purple-card-shadow">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 items-start">
                {/* Left Form */}
                <div className="space-y-4">
                    <div className="flex items-center gap-3">
                        <div className="ml-auto flex gap-2 items-center">
                                <button
                                    type="button"
                                    onClick={() => setIsRented(true)}
                                    aria-pressed={isRented}
                                    className={`px-3 py-1 rounded-md border transition-colors duration-150 ${isRented ? 'bg-[#0b3b82] text-white border-[#0b3b82] shadow-sm' : 'bg-white text-slate-700'}`}
                                >
                                    Yes
                                </button>
                                <button
                                    type="button"
                                    onClick={() => setIsRented(false)}
                                    aria-pressed={!isRented}
                                    className={`px-3 py-1 rounded-md border transition-colors duration-150 ${!isRented ? 'bg-[#0b3b82] text-white border-[#0b3b82] shadow-sm' : 'bg-white text-slate-700'}`}
                                >
                                    No
                                </button>
                        </div>
                    </div>

                    <div>
                        <label className="block text-sm font-semibold mb-1">Purchase Price (₹)*</label>
                        <input type="number" value={purchasePrice} onChange={(e) => setPurchasePrice(Number(e.target.value))} className="w-full border rounded p-3 text-sm" />
                        <div className="text-[12px] text-slate-500 mt-1">{formatCompact(purchasePrice)}</div>
                    </div>

                    <div>
                        <label className="block text-sm font-semibold mb-1">Current Price of Property (₹)*</label>
                        <input type="number" value={currentPrice} onChange={(e) => setCurrentPrice(Number(e.target.value))} className="w-full border rounded p-3 text-sm" />
                        <div className="text-[12px] text-slate-500 mt-1">{formatCompact(currentPrice)}</div>
                    </div>

                    {isRented && (
                        <div>
                            <label className="block text-sm font-semibold mb-1">Rent Per Month (₹)*</label>
                            <input type="number" value={rentPerMonth} onChange={(e) => setRentPerMonth(Number(e.target.value))} className="w-full border rounded p-3 text-sm" />
                            <div className="text-[12px] text-slate-500 mt-1">{formatINR(rentPerMonth) === '₹0' ? '' : formatINR(rentPerMonth)}</div>
                        </div>
                    )}

                    <div>
                        <label className="block text-sm font-semibold mb-1">Purchase Date</label>
                        <input type="month" value={purchaseDate} onChange={(e) => setPurchaseDate(e.target.value)} className="w-full border rounded p-3 text-sm" />
                    </div>
                </div>

                {/* Right Summary */}
                <div>
                    <div className="rounded-xl border-8 border-[#001b4a] p-6 bg-white shadow-md h-full">
                        <div className="flex items-center justify-between mb-4">
                              <h3 className="font-extrabold text-lg text-[#001b4a]">Total Investment</h3>
                              <div className="text-2xl font-extrabold text-[#0b3b82]">{formatCompact(results.totalInvestment)}</div>
                        </div>

                        <div className="border-t border-dashed border-slate-200 pt-4 space-y-3">
                            <div className="flex items-center justify-between">
                                <div className="text-sm text-slate-700">Total Rental Income</div>
                                <div className="font-semibold text-slate-800">{formatCompact(results.totalRentalIncome)}</div>
                            </div>

                            <div className="flex items-center justify-between">
                                <div className="text-sm text-slate-700">Total Rental year</div>
                                <div className="font-semibold text-slate-800">{results.totalRentalYears} years</div>
                            </div>

                            <div className="flex items-center justify-between">
                                <div className="text-sm text-slate-700">Net Profit</div>
                                <div className="font-semibold text-slate-800">{formatCompact(results.netProfit)}</div>
                            </div>

                            <div className="flex items-center justify-between">
                                <div className="text-sm text-slate-700">ROI (%)</div>
                                <div className="font-semibold text-slate-800">{isFinite(results.roiPerc) ? `${results.roiPerc.toFixed(2)}%` : '-'}</div>
                            </div>

                            <div className="flex items-center justify-between">
                                <div className="text-sm text-slate-700">Annualized ROI</div>
                                <div className={`font-semibold ${results.annualized >= 0 ? 'text-emerald-600' : 'text-rose-600'}`}>{results.annualized ? `${(results.annualized * 100).toFixed(2)}%` : '-'}</div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default RealEstateInvestmentCalculator;
